/**
 * Ana sayfa / MainSearchBar — tek kaynak: API tipi vs URL tipi.
 * API (autocomplete, filtreler): ProductTypeField → çoğunlukla `tour`.
 * URL (yönlendirme): private-tours | group-tours | yacht …
 */

import type { PageUrlMap } from '@/types/blog';
import {
  buildCategoryUrlForNavigation,
  buildProductUrlForNavigation,
  resolveFilterApiProductType,
  resolveSearchTabListingType,
} from '@/lib/url-helpers';

export type SearchTabLike = { type?: string; slug?: string } | null | undefined;

/** Autocomplete + getProductFilters için Laravel product_type */
export function mainSearchApiProductType(tab: SearchTabLike, fallback = 'tour'): string {
  const listing = resolveSearchTabListingType(tab ?? undefined) ?? tab?.type ?? fallback;
  return resolveFilterApiProductType(listing);
}

/** Check Availability → path segment */
export function mainSearchUrlListingType(tab: SearchTabLike, fallback = 'private-tours'): string {
  return resolveSearchTabListingType(tab ?? undefined) ?? tab?.type ?? fallback;
}

const DEFAULT_AUTOCOMPLETE_SOURCES = 'category,product,product_id';

export function mainSearchAutocompleteSources(
  fieldSource: string | undefined,
  apiProductType: string,
): string {
  let sources = (fieldSource?.trim() || DEFAULT_AUTOCOMPLETE_SOURCES).includes('product_id')
    ? fieldSource!.trim()
    : `${fieldSource?.trim() || DEFAULT_AUTOCOMPLETE_SOURCES},product_id`.replace(/^,/, '');
  const tourLike = ['tour', 'private-tours', 'group-tours'];
  if (tourLike.includes(apiProductType) && !sources.includes('station_city')) {
    sources = `${sources},station_city`;
  }
  if (apiProductType === 'yacht' && !sources.includes('yacht_id')) {
    sources = `${sources},yacht_id`;
  }
  return sources;
}

export function navigateFromAutocompleteSelection(options: {
  kind: 'category' | 'product';
  slug: string;
  suggestionProductType?: string;
  tab: SearchTabLike;
  pageUrls?: PageUrlMap;
  language: string;
  defaultLanguage: string;
}): string {
  const urlHint = mainSearchUrlListingType(options.tab);
  if (options.kind === 'category') {
    return buildCategoryUrlForNavigation(
      options.slug,
      options.suggestionProductType,
      urlHint,
      options.pageUrls,
      options.language,
      options.defaultLanguage,
    );
  }
  return buildProductUrlForNavigation(
    options.slug,
    options.suggestionProductType,
    urlHint,
    options.pageUrls,
    options.language,
    options.defaultLanguage,
  );
}

/** Autocomplete alan key’leri (birleşik destination,product_id dahil) */
export function collectAutocompleteFieldKeys(
  fields: { field_type?: string; field_key?: string }[],
  selectedSuggestionTypes: Record<string, unknown>,
): string[] {
  const autocompleteFields = fields.filter((f) => f?.field_type === 'autocomplete');
  const keys = new Set<string>();
  autocompleteFields.forEach((field) => {
    (field.field_key ?? '').split(',').forEach((k) => {
      const t = k.trim();
      if (t) keys.add(t);
    });
  });
  if (autocompleteFields.length > 1) {
    keys.add(autocompleteFields.map((f) => f.field_key).join(','));
  }
  Object.keys(selectedSuggestionTypes).forEach((k) => keys.add(k));
  return [...keys];
}

export type AutocompleteSelectionKind = 'category' | 'product' | 'station_city' | 'none';

export interface ResolvedAutocompleteSelection {
  kind: AutocompleteSelectionKind;
  fieldKey?: string;
  slug?: string;
  productType?: string;
}

/**
 * Submit / buton metni: önce kategori, sonra ürün, sonra şehir (tek birincil seçim).
 */
export function resolvePrimaryAutocompleteSelection(
  fieldKeys: string[],
  selectedSuggestionTypes: Record<string, string | undefined>,
  selectedSuggestionSlugs: Record<string, string | undefined>,
  selectedSuggestionProductTypes: Record<string, string | undefined>,
  formValues: Record<string, unknown> = {},
): ResolvedAutocompleteSelection {
  const read = (kind: 'category' | 'product' | 'station_city'): ResolvedAutocompleteSelection | null => {
    for (const key of fieldKeys) {
      if (selectedSuggestionTypes[key] !== kind) continue;
      const raw = selectedSuggestionSlugs[key] || formValues[key];
      if (raw == null || raw === '') continue;
      const slug = typeof raw === 'string' ? raw : String(raw);
      return {
        kind,
        fieldKey: key,
        slug,
        productType: selectedSuggestionProductTypes[key],
      };
    }
    return null;
  };

  return read('category') ?? read('product') ?? read('station_city') ?? { kind: 'none' };
}

function isYachtListingType(listingType: string | undefined): boolean {
  const t = (listingType ?? '').toLowerCase();
  return t === 'yacht' || t.includes('yacht');
}

/**
 * Seçime göre submit butonu metni (language variables + fallback).
 */
export function mainSearchSubmitButtonLabel(
  selectionKind: AutocompleteSelectionKind,
  listingType: string | undefined,
  getVariable: (key: string, fallback: string) => string,
): string {
  const yacht = isYachtListingType(listingType);

  if (selectionKind === 'category') {
    return yacht
      ? getVariable('search_view_all_yachts', 'View All Yachts')
      : getVariable('search_view_tours', 'View Tours');
  }
  if (selectionKind === 'product') {
    return yacht
      ? getVariable('search_view_detail', 'View Detail')
      : getVariable('search_check_availability', 'Check Availability');
  }
  // Şehir / boş arama → liste sayfası
  return yacht
    ? getVariable('search_view_all_yachts', 'View All Yachts')
    : getVariable('search_view_tours', 'View Tours');
}

/** Detay / kategori URL’sine taşınacak tarih & kişi query’leri */
export function buildSearchNavigationQuery(
  filters: Record<string, unknown>,
  appendFilter: (params: URLSearchParams, key: string, value: unknown) => void,
): string {
  const params = new URLSearchParams();
  const allow = new Set([
    'departure_date',
    'check_in_date',
    'check_out_date',
    'pax_count',
    'child_count',
    'child_ages',
  ]);
  Object.entries(filters).forEach(([key, value]) => {
    if (allow.has(key)) appendFilter(params, key, value);
  });
  return params.toString();
}

export function withQueryString(url: string, query: string): string {
  if (!url || url === '#' || !query) return url;
  return url.includes('?') ? `${url}&${query}` : `${url}?${query}`;
}
